import { get, trimEnd, values } from 'lodash';

import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, NavigationExtras, NavigationStart, Params, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { REQUEST } from '@shared/tokens/server.tokens';
import {
  CartState,
  getCart,
  ICart,
  isReturnStepRequired,
  IWebhookUrls,
  CartServiceV2,
  getCartBakery
} from '@cakeiteasy/order-process';
import { CacheService, navigationHelper, UiContextService } from '@cakeiteasy/web-utils';
import { LocationManagerService } from '@cakeiteasy/web-utils';
import { CURRENT_LANG_APP_ROUTES } from '../route-names';
import { take, filter } from 'rxjs/operators';
import { APP_ROUTES } from '@entire/route-names';

export interface ISearchLocationModel {
  name: string;
  postNumber?: string;
  countryCode?: string;
}

@Injectable()
export class NavigationService {

  public lastVisitedCakesOverviewSearch$ = new BehaviorSubject<{
    refName: string, queryParams: any, category?: string, isCity?: boolean
  }>(null);

  public lastAvailableOrderProcessUrl$ = new BehaviorSubject<string>(null);
  public lastVisitedNotAccountPage$ = new BehaviorSubject<string>(null);
  public prevPageUrl$ = new BehaviorSubject<string>(null);
  public isOrderProcess$ = new BehaviorSubject<boolean>(null);

  constructor(@Inject('AppConfig') private _appConfig: any,
              @Inject(REQUEST) private _request: any,
              @Inject(PLATFORM_ID) private _platformId: Object,
              private _uiContext: UiContextService,
              private _cacheService: CacheService,
              private _cartService: CartServiceV2,
              private _router: Router,
              private _locationManager: LocationManagerService,
              private _cartStore: Store<CartState>) {
    this._subscribe();
  }

  private _subscribe() {
    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const terms = ['delivery', 'payment']
          .concat(values(CURRENT_LANG_APP_ROUTES.CATEGORY))
          .concat(values(CURRENT_LANG_APP_ROUTES.PRODUCT));

        const isOrderProcess = Boolean(terms.find((t) => event.urlAfterRedirects.indexOf(t) >= 0));
        this.isOrderProcess$.next(isOrderProcess);
      }

      if (event instanceof NavigationStart) {
        let url = this._router.url;

        if (url.indexOf('/profile') === 0 || url.indexOf('/change-password') === 0) {
          return;
        }

        this.prevPageUrl$.next(url);
        this.lastVisitedNotAccountPage$.next(url);
      }
    });

    this._cartStore.select(getCart).subscribe((cart) => {
      if (!cart) {
        return this.lastAvailableOrderProcessUrl$.next('/');
      }

      if (cart.delivery && cart.delivery.orderId) {
        return this.lastAvailableOrderProcessUrl$.next('payment');
      }

      if (cart.bakery) {
        return this.lastAvailableOrderProcessUrl$.next('delivery');
      }

      this.lastAvailableOrderProcessUrl$.next(null);
    });
  }

  public backToCakesOverview(defaultRefName: string) {
    const searchModel = this.lastVisitedCakesOverviewSearch$.getValue() || { refName: defaultRefName, queryParams: {} };

    if (searchModel) {
      return this.goToProductsOverview(searchModel.refName, searchModel.queryParams, searchModel.category);
    }

    return this.goToLastAvailableOrderProcessStep();
  }

  public goToBakeryCakesOverview(bakeryName) {
    const searchModel = this.lastVisitedCakesOverviewSearch$.getValue() || { queryParams: {}, category: null };
    return this.goToProductsOverview(bakeryName, searchModel.queryParams, searchModel.category);
  }

  public goHome() {
    return this._router.navigate(['/']);
  }

  public goToLandingPage(slug: string) {
    return this._router.navigate(['/', slug]);
  }

  public goToGiftCards() {
    return this._router.navigate(['/', APP_ROUTES.GIFT_CARDS]);
  }

  public generateWebhooksUrls(method: string): IWebhookUrls {
    const url = `${location.protocol}//${location.host}/receipt/pre?method=${method}`;
    const rejectUrl = `${location.protocol}//${location.host}${location.pathname}/pre?method=${method}&error=true`;
    return {
      successUrl: url,
      rejectUrl
    };
  }

  public goToStartNewOrder() {
    return this._cartService.resetCart()
      .then(() => this.goHome());
  }

  public backToOrderProcess() {
    let prevUrl = this.lastVisitedNotAccountPage$.getValue();

    this._router.navigateByUrl(prevUrl || this.lastAvailableOrderProcessUrl$.getValue() || '');
  }

  public goToLastAvailableOrderProcessStep() {
    return this._router.navigateByUrl(this.lastAvailableOrderProcessUrl$.getValue() || '');
  }

  public getLocationUrl(location: { name: string, postNumber?: string, category?: string }, queryParams?: Params): string {
    const transformed = navigationHelper.transformLocationName(location.name);
    let domain = this._locationManager.getDomainName();

    const categorySegmentUrl = CURRENT_LANG_APP_ROUTES.CATEGORY[domain];
    const categoryNameId = location.category || this._appConfig.cakesNameIds[domain];

    return this._router.createUrlTree([transformed, categorySegmentUrl, categoryNameId], {
      queryParams: {
        ...(queryParams || null),
        postNumber: location.postNumber || null
      }
    }).toString();
  }

  public switchToDifferentLocation(location: ISearchLocationModel, queryParams?: Params): Promise<boolean> {
    return this._router.navigateByUrl(this.getLocationUrl(location, queryParams));
  }

  public getMarketplaceBaseUrl(countryCode) {
    return trimEnd(this._appConfig.countryDomainUrls[countryCode], '/');
  }

  public goToProductsOverview(locationName: string, queryParams?: Params, category?: string) {
    return this._router.navigateByUrl(this.getLocationUrl({ name: locationName, category }, queryParams));
  }

  public goToDelivery(extras?: NavigationExtras) {
    const commands = ['/delivery'];

    if (this._uiContext.isMobile()) {
      commands.push('m');
    }

    return this._router.navigate(commands, extras || {});
  }

  public goToReturn(extras?: NavigationExtras) {
    const commands = ['/return'];

    if (this._uiContext.isMobile()) {
      commands.push('m');
    }

    return this._router.navigate(commands, extras || {});
  }

  /**The step after delivery is the return step for carts that need one, and payment for the rest.
   * Callers finishing the delivery step go through here rather than straight to payment.*/
  public goToStepAfterDelivery(cart: ICart, extras?: NavigationExtras) {
    return isReturnStepRequired(cart) ? this.goToReturn(extras) : this.goToPayment(extras);
  }

  public goToStepBeforePayment(cart: ICart, extras?: NavigationExtras) {
    return isReturnStepRequired(cart) ? this.goToReturn(extras) : this.goToDelivery(extras);
  }

  public goToPayment(extras?: NavigationExtras) {
    const commands = ['/payment'];

    if (this._uiContext.isMobile()) {
      commands.push('m');
    }
    return this._router.navigate(commands, extras || {});
  }

  public continueProcess() {
    const categoryTerms = [...values(CURRENT_LANG_APP_ROUTES.CATEGORY)];
    const b2bTerms = [...values(CURRENT_LANG_APP_ROUTES.B2B)];
    const url = this._router.url;

    if (url === '/' || b2bTerms.some(term => url.indexOf(term) !== -1)) {
      return new Promise((resolve) => {
        this._cartStore
          .pipe(select(getCartBakery), filter(b => Boolean(b)), take(1))
          .subscribe(bakery => resolve(this.goToProductsOverview(bakery.name)));
      });
    }

    if (categoryTerms.some(term => url.indexOf(term) !== -1)) {
      return this.goToDelivery();
    }

    if (url.indexOf('delivery') !== -1) {
      return this.goToPayment();
    }
  }

  public getCurrentHref() {
    let href = '';
    if (isPlatformBrowser(this._platformId)) {
      href = window.location.href.split('?')[0];
    } else if (this._request) {
      let { headers, baseUrl, path } = this._request;

      let protocol = this._appConfig.production ? 'https' : 'http';
      href = `${protocol}://` + `${headers['origin'] || headers['host']}${baseUrl}${path}/`;
    }

    return href;
  }

  public goToPreReceipt(sid: string) {
    return this._router.navigate(['receipt', 'pre'], { queryParams: { sid } });
  }

  public goToProfile() {
    return this._router.navigate(['profile']);
  }

  public getStandaloneCakeBuilderUrl(params: { productTypeId: number, productTypeName: string, bakeryName: string },
                                     queryParams?: any) {
    const domainName = this._locationManager.getDomainName();
    const { bakeryName, productTypeName, productTypeId } = params;

    return this._router.createUrlTree(['/',
      navigationHelper.transformLocationName(bakeryName),
      get(CURRENT_LANG_APP_ROUTES.PRODUCT, domainName, 'category'),
      navigationHelper.transformLocationName(productTypeName) + '-' + productTypeId], {
      queryParams
    }).toString();
  }

  public navigateToStandaloneCakeBuilder(params: { productTypeId: number, productTypeName: string, bakeryName: string },
                                         queryParams?: any) {
    return this._router.navigateByUrl(this.getStandaloneCakeBuilderUrl(params, queryParams));
  }
}
